<template>
  <div data-component-group="line-item-form" data-component="line-item-hero">
    <div v-if="isLoading" class="line-item-loading">
      <v-progress-circular color="white" indeterminate :width="3" :size="24" />
    </div>
    <div v-else class="line-item-details">
      <div class="line-item-title">
        <span>{{ lineItemName }}</span>
      </div>

      <div class="line-item-details-table">
        <div class="line-item-details-box">
          <div>
            {{ $t('start_date') }}
          </div>
          <span>
            {{ startDate }}
          </span>
        </div>

        <div class="line-item-details-box">
          <div>
            {{ $t('end_date') }}
          </div>
          <span>
            {{ endDate }}
          </span>
        </div>

        <div class="line-item-details-box">
          <div>
            {{ $t('status') }}
          </div>
          <span v-if="!isLoading">
            <div v-if="isDelivering" class="delivering-status">
              <span class="status-circle" />
              {{ $t('delivering') }}
            </div>
            <span v-else>{{ statusName }}</span>
          </span>
        </div>

        <div class="line-item-details-box">
          <StatusSelect
            title="Change Status"
            type="line"
            :items="lineStatuses"
            :is-disabled="isLoading || isUpdating"
            :active-item="statusName"
            :on-change-item="changeStatus"
          />
        </div>

        <div class="line-item-details-box">
          <div>
            {{ $t('progress') }}
          </div>
          <span> {{ progress }}% </span>
        </div>

        <div class="line-item-details-box">
          <div>
            {{ $t('rate') }}
          </div>
          <span> {{ rate }}€ </span>
        </div>

        <div class="line-item-details-box">
          <div>
            {{ $t('archived') }}
          </div>
          <span>
            {{ archivedText }}
          </span>
        </div>

        <div class="line-item-details-box">
          <div>
            {{ $t('impressions') }}
            <Tooltip
              :text="$t('the_value_describes_the_number')"
              :link="impressionsLink"
              :delay="1000"
              position="top"
            />
          </div>
          <span v-if="!isLoading">
            <div v-if="isDelivering" class="delivering-impressions">
              <v-img src="/img/fetching_data.svg" class="svg-spinner" />
              <FetchingDataTooltip />
            </div>
            <span v-else>
              {{ impressions }}
            </span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import StatusSelect from '../../dropdowns/Status';
  import Tooltip from '../../tooltips/Tooltip';
  import FetchingDataTooltip from '../../tooltips/FetchingDataTooltip';
  import { isObj, isNum, isBool } from '../../../utils';

  export default {
    components: {
      StatusSelect,
      Tooltip,
      FetchingDataTooltip,
    },
    props: {
      order: {
        type: Object,
        required: true,
      },
      lineItem: {
        type: Object,
        required: true,
      },
      isLoading: {
        type: Boolean,
        required: true,
      },
      isUpdating: {
        type: Boolean,
        required: true,
      },
      onChangeStatus: {
        type: Function,
        required: true,
      },
    },
    data() {
      return {
        lineItemName: '',
        startDate: '',
        endDate: '',
        statusName: '',
        archived: '',
        targetStatus: {},
        progress: 0,
        rate: 0,
        impressions: 0,
        impressionsLink: import.meta.env.VUE_APP_HELP_IMPRESSIONS_URL,
        isDelivering: false,
      };
    },
    computed: {
      lineStatuses() {
        return this.$store.getters['promotions/lineStatuses'];
      },
      archivedText() {
        if (!isBool(this.archived)) {
          return '';
        }

        return this.archived ? 'Yes' : 'No';
      },
    },
    watch: {
      lineItem(nextItem) {
        this.clear();
        this.onReceiveLineItem(nextItem);
      },
    },
    created() {
      this.onReceiveLineItem(this.lineItem);
    },
    methods: {
      onReceiveLineItem(nextItem) {
        if (isObj(nextItem) && isNum(nextItem.id)) {
          this.extractLineItemData(nextItem);
        }
      },
      extractLineItemData(lineItem) {
        const isDelivering = this.isDeliveringItem(lineItem);
        const {
          name,
          start_date,
          end_date,
          status,
          progress,
          rate,
          impressions,
          isArchived,
        } = lineItem;

        this.isDelivering = isDelivering;
        this.lineItemName = name;
        this.startDate = start_date;
        this.endDate = end_date;
        this.statusName = status.name;
        this.progress = progress;
        this.impressions = impressions;

        if (rate) {
          this.rate = rate;
        }

        if (isBool(isArchived)) {
          this.archived = isArchived;
        }
      },
      isDeliveringItem(item) {
        return (
          item.status &&
          item.status.name === 'Ready' &&
          this.hasStarted(item.start_date)
        );
      },
      hasStarted(startDate) {
        const minutesSinceStart = this.$dayjs().diff(
          this.$dayjs(startDate),
          'minutes',
        );
        return minutesSinceStart >= 5;
      },
      changeStatus(status) {
        const query = {
          orderId: this.order.id,
          lineItemId: this.lineItem.id,
          statusId: status.id,
        };

        this.onChangeStatus(query);
      },
      clear() {
        this.lineItemName = '';
        this.startDate = '';
        this.endDate = '';
        this.statusName = '';
        this.progress = '';
        this.archived = '';
        this.rate = 0;
        this.impressions = 0;
        this.isDelivering = false;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='line-item-form'][data-component='line-item-hero'] {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-xl;
  background-color: map.get($colors, grey, darken-4);

  @media (max-width: map.get($breakpoints, sm)) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 14px 24px;
  }

  .line-item-loading {
    margin: 16px 0;
    width: 100%;
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .line-item-details {
    margin: 2px 0 16px 0;

    .line-item-title {
      padding-right: 40px;
      font-weight: 500;
      font-size: $text-size-xl;
      line-height: 32px;
      letter-spacing: 0.4px;
      color: map.get($colors, shades, 'white');
    }

    .line-item-details-table {
      width: 590px;
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      color: grey;
      font-size: 12px;

      .line-item-details-box {
        position: relative;
        margin-top: 20px;
        width: 120px;

        [data-component="dropdown"] {
          position: absolute;
          top: 2px;
          left: -16px;
        }

        span {
          font-size: 14px;
          font-weight: 600;
          color: white;
        }

        .green-status {
          color: #43a047;
        }

        .red-status {
          color: red;
        }

        .delivering-status {
          display: flex;
          align-items: center;
          color: #4caf50;

          .status-circle {
            margin-right: 6px;
            width: 6px;
            height: 6px;
            display: inline-block;
            background-color: #4caf50;
            border-radius: 50%;
          }
        }

        .delivering-impressions {
          display: flex;

          .svg-spinner {
            margin-right: 5px;
            max-width: 20px;
          }

          .text {
            color: white;
            font-weight: 400;
            text-decoration: underline;
          }
        }
      }
    }
  }
}
</style>
